import { useEffect, useMemo, useRef, useState } from "react";
import type { InvitationCustomField, InvitationMedia, PublicInvitation } from "@/lib/types";
import ModeButtons from "./animationModes/ModeButtons";
import ModeReveal from "./animationModes/ModeReveal";

const DEFAULT_YES_TITLE = "Yes";
const DEFAULT_NO_TITLE = "No";
const DETAIL_COLUMN_GAP = 10;

type ResponseChoice = "yes" | "no";
type DetailWidth = "half" | "full";
type SmartDetailField = InvitationCustomField & { width: DetailWidth };

export default function InvitationCard({ inv }: { inv: PublicInvitation }) {
  const [responseChoice, setResponseChoice] = useState<ResponseChoice | null>(null);
  const customFields = filledFields(inv.customFields);
  const media = collectInvitationMedia(inv);
  const yesText = inv.ctaYesText?.trim() || "Yes";
  const noText = inv.ctaNoText?.trim() || "No";
  const isRomantic = inv.theme === "romantic";

  return (
    <article
      className={`relative w-full animate-scale-in text-[rgb(var(--text))] ${
        isRomantic ? "romantic-invite max-w-6xl" : "max-w-5xl"
      }`}
    >
      {inv.music && <InvitationMusicControl music={inv.music} romantic={isRomantic} />}

      {isRomantic && <RomanticDecor />}

      <div
        data-invite-safe-area
        className={`relative overflow-hidden border backdrop-blur-2xl ${
          isRomantic
            ? "rounded-[30px] border-white/45 bg-white/24 shadow-[0_28px_90px_-58px_rgba(115,43,79,0.48)] sm:rounded-[38px]"
            : "rounded-[28px] border-[rgb(var(--divider)/0.18)] bg-[rgb(var(--surface)/0.90)] shadow-[0_28px_90px_-46px_rgba(83,28,52,0.65)] sm:rounded-[34px]"
        }`}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background: isRomantic
              ? "radial-gradient(circle at 18% 5%, rgb(255 255 255 / 0.56), transparent 32%), radial-gradient(circle at 88% 18%, rgb(var(--blob-b) / 0.28), transparent 34%), linear-gradient(145deg, rgb(255 255 255 / 0.34), rgb(255 255 255 / 0.12) 48%, rgb(255 236 230 / 0.22))"
              : "radial-gradient(circle at 18% 0%, rgb(var(--accent) / 0.18), transparent 34%), radial-gradient(circle at 92% 12%, rgb(var(--accent-strong) / 0.12), transparent 30%), linear-gradient(180deg, rgb(255 255 255 / 0.20), transparent 38%)"
          }}
        />

        <div
          className={`relative grid gap-0 ${
            isRomantic && media.length === 0
              ? "lg:grid-cols-1"
              : "lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.82fr)]"
          }`}
        >
          <section
            className={`min-w-0 ${
              isRomantic
                ? media.length > 0
                  ? "px-5 py-8 sm:px-9 sm:py-10 lg:px-12 lg:py-14"
                  : "mx-auto w-full max-w-3xl px-5 py-10 sm:px-10 sm:py-14 lg:px-14 lg:py-16"
                : "px-5 py-7 sm:px-8 sm:py-9 lg:px-10 lg:py-11"
            }`}
          >
            {responseChoice ? (
              <ResponseScreen inv={inv} choice={responseChoice} romantic={isRomantic} />
            ) : (
              <>
                <ModeReveal mode={inv.animationMode} title={inv.title} message={inv.message}>
                  <InvitationBody
                    inv={inv}
                    customFields={customFields}
                    romantic={isRomantic}
                    centered={isRomantic && media.length === 0}
                  />
                </ModeReveal>

                <div className={isRomantic ? "mt-6 sm:mt-7" : "mt-7 sm:mt-8"}>
                  <ModeButtons
                    mode={inv.animationMode}
                    yesText={yesText}
                    noText={noText}
                    onYes={() => setResponseChoice("yes")}
                    onNo={() => setResponseChoice("no")}
                  />
                </div>
              </>
            )}
          </section>

          {media.length > 0 && (
            <aside
              className={
                isRomantic
                  ? "border-t border-white/34 p-4 sm:p-5 lg:border-l lg:border-t-0 lg:p-6"
                  : "border-t border-[rgb(var(--divider)/0.14)] p-4 sm:p-5 lg:border-l lg:border-t-0 lg:p-6"
              }
            >
              <MediaSection media={media} romantic={isRomantic} />
            </aside>
          )}
        </div>
      </div>
    </article>
  );
}

function InvitationBody({
  inv,
  customFields,
  romantic = false,
  centered = false
}: {
  inv: PublicInvitation;
  customFields: InvitationCustomField[];
  romantic?: boolean;
  centered?: boolean;
}) {
  return (
    <div
      className={`mx-auto max-w-2xl text-center ${
        centered ? "" : "lg:mx-0 lg:text-left"
      }`}
    >
      {inv.recipientName && (
        <p
          className={
            romantic
              ? "inline-flex rounded-full border border-white/48 bg-white/38 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[rgb(var(--accent-strong))] shadow-[0_10px_32px_-26px_rgba(120,45,78,0.65)] backdrop-blur-xl"
              : "text-sm font-semibold text-[rgb(var(--subtext)/0.78)]"
          }
        >
          For {inv.recipientName}
        </p>
      )}

      <h1
        className={`text-balance font-display font-bold text-[rgb(var(--text))] ${
          romantic
            ? "mt-5 text-[clamp(2.65rem,13vw,6.45rem)] leading-[0.92]"
            : "mt-3 text-[clamp(2.45rem,10vw,5.6rem)] leading-[0.96]"
        }`}
      >
        {inv.title}
      </h1>

      {inv.message && (
        <p
          className={`mx-auto max-w-xl whitespace-pre-wrap text-base text-[rgb(var(--subtext))] sm:text-lg ${
            romantic
              ? `mt-5 leading-7 ${centered ? "" : "lg:mx-0"}`
              : "mt-5 leading-8 lg:mx-0"
          }`}
        >
          {inv.message}
        </p>
      )}

      {customFields.length > 0 && (
        romantic ? (
          <RomanticDetails fields={customFields} />
        ) : (
          <dl className="mt-7 grid gap-2.5 sm:grid-cols-2">
            {customFields.map((field) => (
              <div
                key={field.key}
                className="rounded-[18px] border border-[rgb(var(--divider)/0.16)] bg-white/52 px-4 py-3 text-left shadow-[0_12px_36px_-30px_rgba(80,32,52,0.65)] backdrop-blur-xl"
              >
                <dt className="text-[11px] font-bold uppercase tracking-[0.16em] text-[rgb(var(--accent-strong))]">
                  {field.label}
                </dt>
                <dd className="mt-1 text-sm leading-6 text-[rgb(var(--text))]">
                  {field.value}
                </dd>
              </div>
            ))}
          </dl>
        )
      )}

      {inv.senderName && (
        <p
          className={
            romantic
              ? "mt-6 inline-flex rounded-full border border-white/44 bg-white/30 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[rgb(var(--subtext)/0.74)] backdrop-blur-xl"
              : "mt-8 text-sm font-semibold text-[rgb(var(--subtext)/0.82)]"
          }
        >
          From {inv.senderName}
        </p>
      )}
    </div>
  );
}

function RomanticDetails({ fields }: { fields: InvitationCustomField[] }) {
  const containerRef = useRef<HTMLDListElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const updateWidth = () => setContainerWidth(node.clientWidth);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const rows = useMemo(
    () => buildSmartDetailRows(fields, containerWidth),
    [fields, containerWidth]
  );

  return (
    <dl
      ref={containerRef}
      className="mt-7 flex w-full flex-col gap-2.5 text-left"
    >
      {rows.map((row, rowIndex) => (
        <div
          key={`${row.map((field) => field.key).join("-")}-${rowIndex}`}
          className="flex w-full gap-2.5"
        >
          {row.map((field) => (
            <RomanticDetailItem key={field.key} field={field} />
          ))}
        </div>
      ))}
    </dl>
  );
}

function RomanticDetailItem({ field }: { field: SmartDetailField }) {
  return (
    <div
      className={`min-w-0 rounded-[16px] border border-white/44 bg-white/30 px-3.5 py-3 text-left shadow-[0_12px_34px_-32px_rgba(80,32,52,0.66)] backdrop-blur-xl ${
        field.width === "half" ? "flex-1" : "w-full"
      }`}
    >
      <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-[rgb(var(--accent-strong))]/75">
        {compactDetailLabel(field.label)}
      </dt>
      <dd className="mt-1 [overflow-wrap:anywhere] text-[13.5px] leading-5 text-[rgb(var(--text))]">
        {field.value}
      </dd>
    </div>
  );
}

function buildSmartDetailRows(fields: InvitationCustomField[], containerWidth: number) {
  const safeWidth = containerWidth || 340;
  const halfWidth = (safeWidth - DETAIL_COLUMN_GAP) / 2;
  const rows: SmartDetailField[][] = [];
  let pending: SmartDetailField | null = null;

  fields.forEach((field) => {
    const width = detailFitsHalf(field, halfWidth) ? "half" : "full";
    const item: SmartDetailField = { ...field, width };

    if (width === "full") {
      if (pending) {
        rows.push([{ ...pending, width: "full" }]);
        pending = null;
      }
      rows.push([item]);
      return;
    }

    if (pending && detailPairFitsHalf(pending, item, halfWidth)) {
      rows.push([pending, item]);
      pending = null;
      return;
    }

    if (pending) {
      rows.push([{ ...pending, width: "full" }]);
    }
    pending = item;
  });

  if (pending !== null) {
    const lastPending: SmartDetailField = pending;
    rows.push([{ ...lastPending, width: "full" }]);
  }
  return rows;
}

function detailFitsHalf(field: InvitationCustomField, halfWidth: number) {
  if (halfWidth < 132) return false;

  const label = normalizedDetailLabel(field.label);
  const value = field.value.trim();
  const count = value.length;
  const hasHardBreak = /[\n;]/.test(value);
  const isCompactKind =
    label.includes("date") ||
    label.includes("time") ||
    label.includes("pickup") ||
    label.includes("dress") ||
    label.includes("attire");
  const isLongKind =
    label.includes("location") ||
    label.includes("address") ||
    label.includes("reservation") ||
    label.includes("note") ||
    label.includes("place") ||
    label.includes("restaurant") ||
    label.includes("cafe");
  const estimatedWidth = estimateDetailTextWidth(field);

  if (hasHardBreak) return false;

  if (isCompactKind) {
    return count <= 30 && estimatedWidth <= halfWidth * 1.55;
  }

  if (isLongKind) {
    return count <= 18 && estimatedWidth <= halfWidth * 1.08;
  }

  if (estimatedWidth <= halfWidth * 1.25 && count <= 24) {
    return true;
  }

  return count <= 20 && estimatedWidth <= halfWidth * 1.12;
}

function detailPairFitsHalf(first: InvitationCustomField, second: InvitationCustomField, halfWidth: number) {
  return detailFitsHalf(first, halfWidth) && detailFitsHalf(second, halfWidth);
}

function estimateDetailTextWidth(field: InvitationCustomField) {
  const label = compactDetailLabel(field.label).length * 5.8;
  const value = field.value.trim().length * 7.1;
  return Math.max(label, value) + 34;
}

function compactDetailLabel(label: string) {
  const normalized = normalizedDetailLabel(label);
  if (normalized.includes("pickup")) return "Pickup";
  if (normalized.includes("reservation") || normalized.includes("note")) return "Note";
  if (normalized.includes("address")) return "Address";
  if (normalized.includes("dress") || normalized.includes("attire")) return "Dress code";
  if (normalized.includes("location") || normalized.includes("place") || normalized.includes("restaurant") || normalized.includes("cafe")) return "Location";
  if (normalized.includes("time")) return "Time";
  if (normalized.includes("date")) return "Date";
  return label.trim();
}

function normalizedDetailLabel(label: string) {
  return label.trim().toLowerCase();
}

function MediaSection({
  media,
  romantic = false
}: {
  media: InvitationMedia[];
  romantic?: boolean;
}) {
  const first = media[0];

  if (romantic) {
    return <RomanticSlideshow media={media} />;
  }

  if (media.length === 1) {
    return <MediaFrame item={first} priority />;
  }

  return (
    <div className="flex snap-x gap-3 overflow-x-auto pb-2 lg:grid lg:snap-none lg:grid-cols-2 lg:overflow-visible lg:pb-0">
      {media.slice(0, 6).map((item, index) => (
        <div
          key={item.id}
          className={`min-w-[78%] snap-center sm:min-w-[46%] lg:min-w-0 ${
            index === 0 ? "lg:col-span-2" : ""
          }`}
        >
          <MediaFrame item={item} priority={index === 0} />
        </div>
      ))}
    </div>
  );
}

function MediaFrame({ item, priority = false }: { item: InvitationMedia; priority?: boolean }) {
  const caption = item.caption?.trim();
  const frameClass =
    "relative overflow-hidden rounded-[22px] border border-white/55 bg-[rgb(var(--bg)/0.16)] shadow-[0_18px_56px_-38px_rgba(69,25,44,0.72)]";

  if (item.type === "video") {
    return (
      <figure className={frameClass}>
        <video
          poster={item.thumbnailUrl}
          controls
          playsInline
          preload="metadata"
          className="aspect-video w-full bg-black object-contain"
        >
          <source src={item.url} type={item.mimeType || "video/mp4"} />
        </video>
        {caption && <MediaCaption>{caption}</MediaCaption>}
      </figure>
    );
  }

  if (item.type === "audio") {
    return null;
  }

  return (
    <figure className={frameClass}>
      <img
        src={item.url}
        alt={caption ?? ""}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        className="max-h-[420px] w-full object-contain"
      />
      {caption && <MediaCaption>{caption}</MediaCaption>}
    </figure>
  );
}

function MediaCaption({ children }: { children: React.ReactNode }) {
  return (
    <figcaption className="absolute inset-x-3 bottom-3 rounded-full bg-white/78 px-3 py-2 text-xs font-semibold text-[rgb(var(--text))] shadow-sm backdrop-blur-xl">
      {children}
    </figcaption>
  );
}

function RomanticSlideshow({ media }: { media: InvitationMedia[] }) {
  const [index, setIndex] = useState(0);
  const active = media[index];
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!active || active.type === "video") return;
    const id = window.setTimeout(() => {
      setIndex((current) => (current + 1) % media.length);
    }, active.type === "gif" ? 5200 : 4300);
    return () => window.clearTimeout(id);
  }, [active, media.length]);

  useEffect(() => {
    if (!active || active.type !== "video") return;
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.muted = true;
    video.play().catch(() => undefined);
  }, [active]);

  if (!active) return null;

  return (
    <div className="relative mx-auto grid w-full justify-items-center">
      <div className="relative aspect-[16/10] w-full max-w-[640px] overflow-hidden rounded-[18px] bg-transparent shadow-[0_18px_54px_-42px_rgba(83,28,52,0.66)] lg:max-w-[min(640px,42vw)]">
        {active.type === "video" ? (
          <video
            ref={videoRef}
            poster={active.thumbnailUrl}
            autoPlay
            controls
            muted
            playsInline
            preload="metadata"
            onEnded={() => setIndex((current) => (current + 1) % media.length)}
            className="h-full w-full bg-[rgb(var(--text)/0.08)] object-cover"
          >
            <source src={active.url} type={active.mimeType || "video/mp4"} />
          </video>
        ) : (
          <img
            key={active.id}
            src={active.url}
            alt=""
            loading="eager"
            decoding="async"
            className="h-full w-full animate-fade-in object-cover"
          />
        )}
      </div>

      {media.length > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          {media.map((item, itemIndex) => (
            <button
              key={`${item.id}-${item.url}-${itemIndex}`}
              type="button"
              aria-label={`Show media ${itemIndex + 1}`}
              aria-current={itemIndex === index}
              onClick={() => setIndex(itemIndex)}
              className={`h-2.5 rounded-full transition-all ${
                itemIndex === index
                  ? "w-7 bg-[rgb(var(--accent-strong))]"
                  : "w-2.5 bg-[rgb(var(--accent-strong)/0.24)] hover:bg-[rgb(var(--accent-strong)/0.45)]"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ResponseScreen({
  inv,
  choice,
  romantic = false
}: {
  inv: PublicInvitation;
  choice: ResponseChoice;
  romantic?: boolean;
}) {
  const isYes = choice === "yes";
  const title =
    (isYes ? inv.yesResponseTitle : inv.noResponseTitle)?.trim() ||
    (isYes ? DEFAULT_YES_TITLE : DEFAULT_NO_TITLE);
  const message = (isYes ? inv.yesResponseMessage : inv.noResponseMessage)?.trim();
  const gifUrl = isYes ? inv.yesResponseGifUrl : inv.noResponseGifUrl;

  return (
    <div
      className={`mx-auto max-w-2xl animate-fade-in text-center ${
        romantic ? "" : "lg:mx-0 lg:text-left"
      }`}
    >
      {gifUrl && (
        <div className="mx-auto mb-6 max-w-md overflow-hidden rounded-[24px] border border-white/60 shadow-[0_18px_50px_-34px_rgba(69,25,44,0.7)] lg:mx-0">
          <img
            src={gifUrl}
            alt=""
            className="max-h-[360px] w-full object-contain"
            loading="eager"
            decoding="async"
          />
        </div>
      )}

      <h2 className="font-display text-4xl font-bold leading-tight text-[rgb(var(--text))] sm:text-5xl">
        {title}
      </h2>
      {message && (
        <p className="mt-4 max-w-xl whitespace-pre-wrap text-base leading-8 text-[rgb(var(--subtext))] sm:text-lg">
          {message}
        </p>
      )}
    </div>
  );
}

function InvitationMusicControl({
  music,
  romantic = false
}: {
  music: PublicInvitation["music"];
  romantic?: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [available, setAvailable] = useState(true);
  const playableUrl = useMemo(() => {
    if (!music) return undefined;
    return (music.source ?? "builtIn") === "builtIn" ? music.fullAudioUrl || music.url : undefined;
  }, [music]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !playableUrl) return;
    audio.volume = 0.22;

    const start = (preferAutoplay = false) => {
      if (preferAutoplay) audio.muted = true;
      audio.play()
        .then(() => {
          audio.muted = false;
          setPlaying(true);
        })
        .catch(() => {
          audio.muted = false;
          setPlaying(false);
        });
    };

    if (romantic) {
      start(true);
    }

    const retryAutoplay = window.setTimeout(() => start(true), 250);

    const startFromGesture = () => start(false);
    window.addEventListener("pointerdown", startFromGesture, { once: true });
    window.addEventListener("keydown", startFromGesture, { once: true });
    return () => {
      window.clearTimeout(retryAutoplay);
      window.removeEventListener("pointerdown", startFromGesture);
      window.removeEventListener("keydown", startFromGesture);
    };
  }, [playableUrl, romantic]);

  if (!music || !playableUrl || !available) return null;

  return (
    <div className="absolute right-3 top-3 z-20 sm:right-4 sm:top-4">
      <button
        type="button"
        aria-pressed={playing}
        onClick={() => {
          const audio = audioRef.current;
          if (!audio) return;
          if (playing) {
            audio.pause();
            setPlaying(false);
          } else {
            audio.play()
              .then(() => setPlaying(true))
              .catch(() => setAvailable(false));
          }
        }}
        className={
          romantic
            ? "grid h-10 w-10 place-items-center rounded-full border border-white/65 bg-white/48 text-[rgb(var(--accent-strong))] shadow-[0_12px_34px_-24px_rgba(99,34,67,0.72)] backdrop-blur-xl transition hover:bg-white/72"
            : "rounded-full border border-white/70 bg-white/72 px-3 py-2 text-xs font-bold text-[rgb(var(--text))] shadow-sm backdrop-blur-xl transition hover:bg-white/90"
        }
      >
        {romantic ? <MusicIcon playing={playing} /> : playing ? "Pause music" : "Play music"}
      </button>
      <audio
        ref={audioRef}
        src={playableUrl}
        autoPlay={romantic}
        muted={romantic}
        preload={romantic ? "auto" : "none"}
        loop
        onPause={() => setPlaying(false)}
        onPlay={(event) => {
          if (romantic) event.currentTarget.muted = false;
          setPlaying(true);
        }}
        onError={() => setAvailable(false)}
      />
    </div>
  );
}

function MusicIcon({ playing }: { playing: boolean }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      className="h-[18px] w-[18px]"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {playing ? (
        <>
          <path d="M8 5v14" />
          <path d="M16 5v14" />
        </>
      ) : (
        <>
          <path d="M9 18V5l10-2v13" />
          <circle cx="7" cy="18" r="3" />
          <circle cx="17" cy="16" r="3" />
        </>
      )}
    </svg>
  );
}

function RomanticDecor() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]">
      <span className="romantic-orbit romantic-orbit-one" />
      <span className="romantic-orbit romantic-orbit-two" />
      <span className="romantic-mark romantic-mark-heart">♡</span>
      <span className="romantic-mark romantic-mark-star">✧</span>
      <span className="romantic-mark romantic-mark-spark">✦</span>
      <span className="romantic-float romantic-float-one">♡</span>
      <span className="romantic-float romantic-float-two">✧</span>
      <span className="romantic-float romantic-float-three">✦</span>
      <span className="romantic-float romantic-float-four">♡</span>
      <span className="romantic-float romantic-float-five">✧</span>
      <span className="romantic-float romantic-float-six">♡</span>
    </div>
  );
}

function collectInvitationMedia(inv: PublicInvitation): InvitationMedia[] {
  const explicit = Array.isArray(inv.media) ? inv.media : [];
  const photos = (Array.isArray(inv.photos) ? inv.photos : []).map((photo) => ({
    id: photo.id,
    type: "photo" as const,
    url: photo.url,
    caption: photo.caption
  }));
  const seen = new Set<string>();

  return [...explicit, ...photos].filter((item) => {
    if (!item.url || item.type === "audio") return false;
    const key = `${item.type}:${item.url}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function filledFields(fields: InvitationCustomField[] | undefined): InvitationCustomField[] {
  return (Array.isArray(fields) ? fields : []).filter(
    (field) => field.value.trim().length > 0
  );
}
